/*eslint max-lines-per-function: ["error", 200]*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Form } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridRecord,
} from '../../../common/components/Grid/GridDataTypes';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { useTranslation } from '../../../common/hooks/useTranslation';
import {
  RecommendedPublisherScopeEnum,
  RecommendedPublisherScopeObj,
} from '../../../common/pokCore/validation/enums';
import FormRow from '../../../app/components/FormRow';
import CompanySelector from '../Selects/CompanySelector';
import PurchaserSelector from '../Selects/PurchaserSelector';
import { PermissionsEnum } from '../../../common/pokCore/authorization/permissions';
import { GetClientExtendedDto } from '../../../common/pokCore/autogenerated/pokApiClient';

interface RecommendedPublisherEditorFormProps {
  profileId: string;
  profileName?: string;
  scopeType: RecommendedPublisherScopeEnum;
  companyId?: string;
  purchaserId?: string;
  selectedClientIds: string[];
  lockedClientIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  readOnly?: boolean;
}

export const RecommendedPublisherEditorForm: React.FC<
  RecommendedPublisherEditorFormProps
> = props => {
  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('administrationLng');
  const [availablePublishers, setAvailablePublishers] = useState<
    GetClientExtendedDto[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (props.profileId) {
      setLoading(true);
      pok.recommendedPublishers
        .getAvailablePublishers(props.profileId)
        .then(publishers => {
          setAvailablePublishers(publishers);
          setLoading(false);
        })
        .catch(errorResponse => {
          notifications.caughtError(errorResponse);
          setLoading(false);
        });
    }
  }, [props.profileId, pok.recommendedPublishers, notifications]);

  const columns: GridColumn[] = useMemo(
    () => [
      { header: t(tk.recommendedPublisher.publisherName), property: 'name' },
      { header: t(tk.common.nip), property: 'nip' },
      { header: t(tk.common.shortname), property: 'shortname' },
      { header: t(tk.recommendedPublisher.scope), property: 'scope' },
    ],
    [t, tk],
  );

  const handleRowClick = (key: string) => {
    if (props.readOnly || props.lockedClientIds.includes(key)) {
      return;
    }
    const isSelected = props.selectedClientIds.includes(key);
    const newSelected = isSelected
      ? props.selectedClientIds.filter(id => id !== key)
      : [...props.selectedClientIds, key];
    props.onSelectionChange(newSelected);
  };

  const getScopeLabel = useCallback(
    (scope: RecommendedPublisherScopeEnum): string =>
      t(RecommendedPublisherScopeObj[scope]),
    [t],
  );

  const data: GridRecord[] = useMemo(() => {
    return availablePublishers.map(publisher => {
      const isLocked = props.lockedClientIds.includes(publisher.id);
      const isSelected = props.selectedClientIds.includes(publisher.id);
      let scopeLabel = '';
      if (isLocked) {
        scopeLabel = t(tk.recommendedPublisher.inherited);
      } else if (isSelected) {
        scopeLabel = getScopeLabel(props.scopeType);
      }

      return {
        key: publisher.id,
        values: {
          name: publisher.name,
          nip: publisher.nip || '-',
          shortname: publisher.shortname,
          scope: scopeLabel,
        },
        readOnly: isLocked,
      };
    });
  }, [
    availablePublishers,
    props.lockedClientIds,
    props.selectedClientIds,
    props.scopeType,
    t,
    tk.recommendedPublisher.inherited,
    getScopeLabel,
  ]);

  const allSelectedKeys = useMemo(
    () => [...props.lockedClientIds, ...props.selectedClientIds],
    [props.lockedClientIds, props.selectedClientIds],
  );

  const allUniqueKeys = useMemo(
    () => Array.from(new Set(allSelectedKeys)),
    [allSelectedKeys],
  );

  const allUniqueLockedKeys = useMemo(
    () => Array.from(new Set(props.lockedClientIds)),
    [props.lockedClientIds],
  );

  return (
    <div>
      <Form className="d-grid gap-3 mb-3">
        <FormRow controlId="profile" label={t(tk.administration.profile)}>
          <Form.Control
            type="text"
            value={props.profileName}
            readOnly
            disabled
          />
        </FormRow>
        <FormRow controlId="scopeType" label={t(tk.recommendedPublisher.scope)}>
          <Form.Control
            type="text"
            value={getScopeLabel(props.scopeType)}
            readOnly
            disabled
          />
        </FormRow>
        {props.scopeType !== RecommendedPublisherScopeEnum.Global && (
          <FormRow controlId="company" label={t(tk.common.company)}>
            <CompanySelector
              value={props.companyId || ''}
              onChange={() => {}}
              readOnly={true}
            />
          </FormRow>
        )}
        {props.scopeType === RecommendedPublisherScopeEnum.Purchaser && (
          <FormRow controlId="purchaser" label={t(tk.common.purchaser)}>
            <PurchaserSelector
              value={props.purchaserId || ''}
              onChange={() => {}}
              readOnly={true}
              role={PermissionsEnum.Administration}
            />
          </FormRow>
        )}
      </Form>
      <Alert variant="info" className="mb-4">
        {t(tk.recommendedPublisher.instruction)}
        <ul className="mt-2 mb-0">
          <li>
            {t(tk.recommendedPublisher.instructionPurchaser)}
            <strong>
              {t(tk.recommendedPublisher.instructionPurchaserDetails)}
            </strong>
          </li>
          <li>
            {t(tk.recommendedPublisher.instructionCompany)}
            <strong>
              {t(tk.recommendedPublisher.instructionCompanyDetails)}
            </strong>
          </li>
          <li>
            {t(tk.recommendedPublisher.instructionGlobal)}{' '}
            <strong>
              {t(tk.recommendedPublisher.instructionGlobalDetails)}
            </strong>
          </li>
        </ul>
      </Alert>
      <div className="mt-4">
        <div className="selected-count">
          {t(tk.component.selected)}:{' '}
          <b>{allUniqueKeys.length - allUniqueLockedKeys.length}</b>
          {'       '}
          {t(tk.recommendedPublisher.inherited2)}:{' '}
          <b>{allUniqueLockedKeys.length}</b>
          {'       '}
          {t(tk.recommendedPublisher.summary)}: <b>{allUniqueKeys.length}</b>
          <br />
          {t(tk.recommendedPublisher.availablePublishers)}{' '}
          <small className="text-muted">
            {t(tk.recommendedPublisher.clickToSelectDeselect)}
          </small>
        </div>
        {loading ? (
          <Skeleton />
        ) : (
          <Grid
            data={data}
            columns={columns}
            showFilter={true}
            availablePageSizes={[10, 20, 50]}
            emptyGridMessage={t(tk.recommendedPublisher.noAvailablePublishers)}
            onRowClick={handleRowClick}
            initialOrderColumn="scope"
            initialOrderDirection="DESC"
            enableMultiSelect={true}
            withFilterInColumns={true}
            selectedKeys={allSelectedKeys}
          />
        )}
      </div>
    </div>
  );
};
